import {
  mapNodeDefinitionToNodeWithView,
  type SearchOperator,
} from '@cognite/apm-client';
import type {
  CogniteClient,
  FilterDefinition,
  ViewReference,
} from '@cognite/sdk';

import { isDirectRelationRef } from '../../idm-observations/enrich-direct-relations-utils';
import { getViewPropertyPath } from '../../idm-observations/observation-filter-utils';
import type { ObservationViewProperty } from '../../idm-observations/types';
import { isEnumProperty } from '../../idm-observations/utils';
import {
  buildSpaceFilter,
  combineSearchFilters,
  encodeDirectRelationFilterValue,
} from '../utils/utils';

import type {
  AggregateFilterOptionsParams,
  FilterOptionWithCount,
  IIdmSearchService,
  SearchInstancesParams,
} from './idm-search-service.interface';

// TODO(FO-2551): Remove once `operator` is added to @cognite/sdk search params.
export type InstancesSearchParamsWithOperator = Parameters<
  CogniteClient['instances']['search']
>[0] & { operator?: SearchOperator };

// TODO(FO-3739): Remove once `operator` is added to @cognite/sdk aggregate params
export type InstancesAggregateParamsWithOperator = Parameters<
  CogniteClient['instances']['aggregate']
>[0] & { operator?: SearchOperator };

export type AggregateFilterContext = {
  viewReference: ViewReference;
  viewProperty: ObservationViewProperty;
  spaces?: string[];
  baseFilter?: FilterDefinition;
  query?: string;
};

export interface CogniteClientDependencies {
  instances: {
    search: (
      params: InstancesSearchParamsWithOperator
    ) => ReturnType<CogniteClient['instances']['search']>;
    aggregate: (
      params: InstancesAggregateParamsWithOperator
    ) => ReturnType<CogniteClient['instances']['aggregate']>;
  };
}

export class IdmSearchService implements IIdmSearchService {
  private cogniteClient: CogniteClientDependencies;

  constructor(client: CogniteClientDependencies) {
    this.cogniteClient = client;
  }

  /**
   * Search instances by given properties within a given view.
   */
  public async searchInstances({
    query,
    viewReference,
    filter,
    properties,
    limit,
    sort,
  }: SearchInstancesParams) {
    const searchResults = await this.cogniteClient.instances
      .search({
        view: viewReference,
        query,
        properties,
        instanceType: 'node',
        limit,
        filter,
        operator: 'AND',
        sort,
      })
      .then((res) =>
        res.items.map((node) =>
          mapNodeDefinitionToNodeWithView(node, viewReference)
        )
      );

    return searchResults;
  }

  /**
   * Discovers filter dropdown options for a view property, each with a count of
   * matching instances in the current search/filter scope.
   *
   * Routes by property type: enum properties use
   * {@link aggregateFilterOptionsPerValue} (one aggregate per predefined option);
   * all other properties use {@link aggregateFilterOptionsByGroupBy} (single
   * `groupBy` aggregate, or a stable-list merge when `baseFilter` / `query` are
   * set).
   *
   * Enum options always include every predefined value (counts can be zero).
   * Non-enum `groupBy` types keep the unscoped option list and apply scoped
   * counts when `baseFilter` or `query` is present.
   *
   * Counts respect `query`, space filters, and `baseFilter` so they match what
   * the user would see in the grid.
   *
   * @param params.viewReference - View whose instances are aggregated.
   * @param params.viewProperty - Property to list filter values for.
   * @param params.spaces - Optional space filter applied before counting.
   * @param params.baseFilter - Optional filter (e.g. column filters) applied before counting.
   * @param params.query - Optional search query applied before counting.
   * @param params.limit - Max distinct values for non-enum `groupBy` (default `1000`; ignored for enums).
   */
  public async aggregateFilterOptions({
    viewReference,
    viewProperty,
    spaces,
    baseFilter,
    query,
    limit = 1000,
  }: AggregateFilterOptionsParams): Promise<FilterOptionWithCount[]> {
    const context: AggregateFilterContext = {
      viewReference,
      viewProperty,
      spaces,
      baseFilter,
      query,
    };

    if (isEnumProperty(viewProperty)) {
      return this.aggregateFilterOptionsPerValue(context);
    }

    if (this.hasScopedFilters(context)) {
      return this.aggregateFilterOptionsByGroupByWithStableList(context, limit);
    }

    return this.aggregateFilterOptionsByGroupBy(context, limit);
  }

  private hasScopedFilters(context: AggregateFilterContext): boolean {
    return Boolean(context.baseFilter) || Boolean(context.query);
  }

  private async countInstances(
    context: AggregateFilterContext,
    valueFilter?: FilterDefinition
  ): Promise<number> {
    const filter = combineSearchFilters(
      buildSpaceFilter(context.spaces),
      context.baseFilter,
      valueFilter
    );

    const { items } = await this.cogniteClient.instances.aggregate({
      view: context.viewReference,
      instanceType: 'node',
      aggregates: [{ count: { property: 'externalId' } }],
      filter,
      query: context.query ?? '',
      limit: 1,
      operator: 'AND',
    });

    const countAggregate = items[0]?.aggregates?.[0];
    return countAggregate && 'value' in countAggregate
      ? Number(countAggregate.value)
      : 0;
  }

  /**
   * Discovers filter dropdown options for enum properties by counting instances
   * per predefined option (`equals` on each option id, one `instances.aggregate`
   * call per option).
   *
   * Returns every entry from `viewProperty.options` with a count of matching
   * instances. Use this when the property has a fixed enum list.
   *
   * Workaround until enum `groupBy` is supported in aggregate (FO-3741).
   *
   * Counts respect `context.query`, space filters, and `context.baseFilter` so
   * they match what the user would see in the grid. Option `value` is the enum
   * option id; `label` comes from the option display name in the view definition.
   *
   * @param context - View, property, and search/filter scope.
   */
  async aggregateFilterOptionsPerValue(
    context: AggregateFilterContext
  ): Promise<FilterOptionWithCount[]> {
    const propertyPath = getViewPropertyPath(
      context.viewReference,
      context.viewProperty.id
    );

    const enumOptions: { value: string; label: string }[] =
      context.viewProperty.options?.map((option) => ({
        value: option.id,
        label: option.name,
      })) ?? [];

    const results = await Promise.all(
      enumOptions.map(async ({ value, label }) => {
        const valueFilter: FilterDefinition = {
          equals: {
            property: propertyPath,
            value,
          },
        };

        const count = await this.countInstances(context, valueFilter);
        return { value, label, count };
      })
    );

    return results;
  }

  /**
   * Keeps the full unscoped `groupBy` option list while applying scoped counts
   * from a second aggregate when `baseFilter` or `query` is active.
   */
  async aggregateFilterOptionsByGroupByWithStableList(
    context: AggregateFilterContext,
    limit: number
  ): Promise<FilterOptionWithCount[]> {
    const unscopedContext: AggregateFilterContext = {
      ...context,
      baseFilter: undefined,
      query: undefined,
    };

    const [allOptions, filteredOptions] = await Promise.all([
      this.aggregateFilterOptionsByGroupBy(unscopedContext, limit),
      this.aggregateFilterOptionsByGroupBy(context, limit),
    ]);
    const countByValue = new Map<string, number>();
    filteredOptions.forEach((option) => {
      countByValue.set(option.value, option.count);
    });

    return allOptions.map((option) => ({
      ...option,
      count: countByValue.get(option.value) ?? 0,
    }));
  }

  /**
   * Discovers filter dropdown options for a view property by grouping instances
   * in one `instances.aggregate` call (`groupBy: [propertyId]`).
   *
   * Returns each distinct property value seen in the current result set, with a
   * count of matching instances. Use this for properties that do not have a
   * fixed enum list (e.g. direct relations such as asset, or free-text fields
   * like `sourceCreatedUser`). For enum properties with predefined options, use
   * {@link aggregateFilterOptionsPerValue} instead (one count query per option).
   *
   * Counts respect `context.query`, space filters, and `context.baseFilter` so
   * they match what the user would see in the grid. Direct relation values are
   * encoded as `space::externalId` for filter definitions; other values use
   * `String(groupValue)` for both value and label.
   *
   * @param context - View, property, and search/filter scope.
   * @param limit - Maximum number of distinct values the aggregate API may return.
   */
  async aggregateFilterOptionsByGroupBy(
    context: AggregateFilterContext,
    limit: number
  ): Promise<FilterOptionWithCount[]> {
    const { viewProperty } = context;
    const filter = combineSearchFilters(
      buildSpaceFilter(context.spaces),
      context.baseFilter
    );

    const { items } = await this.cogniteClient.instances.aggregate({
      view: context.viewReference,
      instanceType: 'node',
      groupBy: [viewProperty.id],
      aggregates: [{ count: { property: 'externalId' } }],
      filter,
      query: context.query,
      limit,
      operator: 'AND',
    });

    return items.flatMap((item) => {
      const groupValue = item.group?.[viewProperty.id];
      const countAggregate = item.aggregates?.[0];
      const count =
        countAggregate && 'value' in countAggregate
          ? Number(countAggregate.value)
          : 0;

      if (groupValue === undefined || groupValue === null) {
        return [];
      }

      if (isDirectRelationRef(groupValue)) {
        const value = encodeDirectRelationFilterValue(groupValue);
        return [
          {
            value,
            label: groupValue.externalId,
            count,
          },
        ];
      }

      const value = String(groupValue);
      return [{ value, label: value, count }];
    });
  }

  public async aggregateCount(
    view: ViewReference,
    filter: FilterDefinition
  ): Promise<number> {
    const { items } = await this.cogniteClient.instances.aggregate({
      view,
      instanceType: 'node',
      aggregates: [{ count: { property: 'externalId' } }],
      filter,
    });

    const countAggregate = items[0]?.aggregates?.[0];
    return countAggregate && 'value' in countAggregate
      ? Number(countAggregate.value)
      : 0;
  }
}

export const createIdmSearchService = (
  ...args: ConstructorParameters<typeof IdmSearchService>
): IIdmSearchService => new IdmSearchService(...args);
